import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { SensorBatteryStats } from 'src/api'
import { useApiInstance } from './useApiInstance'
import { buildRequestParams, ObjectType, transformPage, transformPerPage } from './helpers'

interface BatteryStatsGetParams {
  limit: number
  offset: number
  activationStatus: string
  isAvailableInThingspace: boolean
  stateId: string
}

interface RequestParams {
  params?: Partial<BatteryStatsGetParams> & Record<string, any>
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>
  options?: Omit<UseQueryOptions<unknown, Error, APIList<SensorBatteryStats>>, 'queryKey' | 'queryFn'>
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  activationStatus: 'string',
  isAvailableInThingspace: 'boolean',
  stateId: 'string'
}

export const useSensorBatteryStatsListRequest = ({ params = {}, paramsTransform = [], options }: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [transformPerPage, transformPage, ...paramsTransform].reduce((param, transform) => transform(param), params)
  const queryParams = buildRequestParams<BatteryStatsGetParams>(transformedParams, schema)
  return useQuery<unknown, Error, APIList<SensorBatteryStats>>(
    [Entities.SensorBatteryStats, JSON.stringify(params)],
    async () => {
      const { limit, offset, activationStatus, isAvailableInThingspace, stateId } = queryParams
      const { data } = await api.v1AdminSensorsBatteryStatsGet(
        limit,
        offset,
        activationStatus && activationStatus !== 'all' ? activationStatus : undefined,
        String(params.isAvailableInThingspace) !== 'all' ? isAvailableInThingspace : undefined,
        stateId
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options
    }
  )
}
